import { computed, inject, Injectable, OnInit, signal } from '@angular/core';

import { AppLocalStorageService } from 'src/app/features/shared/storage/services/local-storage-services/app-local-storage.service';
import { ILevelGeneralListDto } from 'src/app/infrastructure/models/level-general-list-dto';
import { ISortField } from 'src/app/infrastructure/pipes/filter.pipe';
import { ITabDataRequest } from '../models/tab-data-request';
import { LIST_KIND_NAME } from 'src/app/infrastructure/services/list-services/_list.service';
import { TabListStorageService } from 'src/app/features/shared/storage/services/storage-services/tab-list-storage.service';
import { TabStorage } from '../../storage/models/tab-data-storage';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { TabListService } from './tab-list.service';
import { BehaviorSubject } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable()
export class TabDataService
{

    private readonly __tabListStorageService = injectRequired(TabListStorageService);
    private readonly __tabListService = injectRequired(TabListService);
    private readonly __appLocalStorageService = injectRequired(AppLocalStorageService);
    // Identiteit voor de OPGESLAGEN gebruikersvoorkeuren hieronder (sortFieldsStored$$/fieldOrder$$) —
    // bewust LIST_KIND_NAME (per-component vast, zie LIST_KIND_NAME-provider naast elke
    // XxxTablistComponent) en niet tabName$$ (= tabData$$()?.name, de TabDataTemplate-naam). Die twee
    // vallen meestal samen, maar tabName$$ is ook de Page_Read-claim-identiteit (zie TabListService.
    // __sourceList_checkAuth) en kan om auth-redenen gedeeld zijn tussen tabs die als voorkeur-"lijst-
    // soort" juist niet hetzelfde zijn (bv. Home's "domains"-tab vs. Domain's "domain users"-tab delen
    // tabName.domainUsers voor de claim, maar zijn qua sortering/veldvolgorde principieel verschillend
    // — zie het commentaar bij __domainsTab in home.component.ts).
    private readonly __listName = injectRequired(LIST_KIND_NAME);

    private __tabStorage$$ = this.__tabListStorageService.tabData$$;

    tabName$$ = computed(() => this.tabData$$()?.name ?? null);
    get TabName() { return this.tabName$$(); }

    private __tabData$ = new BehaviorSubject<ITabDataRequest | null>(null);
    readonly tabData$$ = toSignal(this.__tabData$, { initialValue: null });

    activeGuid$$ = computed(() => this.__tabStorage$$()?.activeGuid ?? null);
    get ActiveGuid(): string | null {return this.activeGuid$$() ?? null;}
    set ActiveGuid(value: ILevelGeneralListDto | null) {
        this.__tabStorage$$?.update(tabStorage => ({...tabStorage, activeGuid: value?.guid ?? null}));
    }

    scrollPos$$ = computed(() => this.__tabStorage$$()?.scrollPos ?? null);
    get ScrollPos(): number | null {return this.scrollPos$$() ?? null;}
    set ScrollPos(value: number | null) {
        this.__tabStorage$$?.update(tabStorage => ({...tabStorage, scrollPos: value ?? null}));
    }
    
    filterString$$ = computed(() => this.__tabStorage$$()?.filterString ?? null);
    get FilterString(): string | null {return this.filterString$$() ?? null;}
    set FilterString(value: string | null) {
        this.__tabStorage$$?.update(tabStorage => ({...tabStorage, filterString: value ?? null}));
    }

    // Volledige verzameling beschikbare filtervelden. SSOT = de tab-definitie (sourceList), net als
    // de lijst-pipe die oorspronkelijk `tabData$$()?.searchFields` gebruikte. (De storage-kopie
    // `__tabStorage$$().searchFields` is hiervoor onbetrouwbaar/leeg.)
    searchFields$$ = computed(() => this.tabData$$()?.searchFields ?? []);

    // Door de gebruiker aangevinkte subset waarop daadwerkelijk gefilterd wordt; persisteert in localStorage.
    // Een lege/ongezette opslag betekent "nog geen keuze" → val terug op alle beschikbare velden
    // (de reset-to-all in searchField_toggle garandeert dat een echte keuze nooit leeg is).
    searchFieldsActive$$ = computed(() => {
        const _stored = this.__tabStorage$$()?.searchFieldsActive;
        return _stored?.length ? _stored : (this.tabData$$()?.searchFields ?? []);
    });
    get SearchFieldsActive(): string[] {return this.searchFieldsActive$$();}
    set SearchFieldsActive(value: string[]) {
        this.__tabStorage$$?.update(tabStorage => ({...tabStorage, searchFieldsActive: value}));
    }

    // Vinkt een veld aan/uit. Het uitvinken van het láátste actieve veld herstelt de volledige set
    // (volledige lijst terug), zodat er nooit een lege filter-selectie ontstaat.
    searchField_toggle(field: string) {
        const _all = this.searchFields$$();
        const _active = this.searchFieldsActive$$();

        if (_active.includes(field)) {
            const _next = _active.filter(f => f !== field);
            this.SearchFieldsActive = _next.length ? _next : [..._all];
        } else {
            this.SearchFieldsActive = _all.filter(f => _active.includes(f) || f === field);
        }
    }

    // Sorteerketen: geordende lijst, positie = prioriteit (1e veld primair, 2e tie-breaker, enz.).
    // In tegenstelling tot de rest van de tab-state (filter/scroll/actieve rij, zie __tabStorage$$
    // hierboven) is dit bewust NIET per bezocht Domain/Project opgeslagen maar globaal per lijst-naam
    // (appData$$.listSortFields, zie app-storage-tree.interface.ts, sleutel = __listName hierboven)
    // — "hoe ik deze lijst-SOORT wil sorteren" is een voorkeur, geen per-bezoek-state, dus geldt
    // hetzelfde ongeacht welk Domain/Project je bekijkt.
    //
    // EXPLICIET (nooit aangevuld met de tab-default) — dit is de bron voor de instellingen-UI
    // (orderedFields$$/sortDirection in filter.component.ts, via FilterService) én voor
    // sortField_toggle's eigen state hieronder. Zonder deze scheiding van de effectieve sortFields$$
    // (met terugval, zie verderop) leken twee dingen kapot zodra een tab een default-sortering heeft
    // (bv. DomainUsers op `active`): (1) na een reset viel de keten meteen weer terug op diezelfde
    // default, waardoor de instellingenlijst zich ongewenst herschikte naar dat veld i.p.v. stabiel
    // te blijven; (2) het bovenste veld leek soms niet te reageren op een klik, omdat het al vóór elke
    // gebruikersactie als "descending" toonde (de default), waardoor de eerste klik meteen de
    // reset-tak trof i.p.v. eerst ascending te worden.
    sortFieldsStored$$ = computed<ISortField[]>(() => {
        return this.__appLocalStorageService.appData$$.listSortFields()?.[this.__listName] ?? [];
    });

    // EFFECTIEF — alleen voor de daadwerkelijke lijst-rendering (de filterSearch-pipe): valt terug op
    // de tab-default zodra de gebruiker niets (meer) expliciet gekozen heeft.
    sortFields$$ = computed<ISortField[]>(() => {
        const _stored = this.sortFieldsStored$$();
        return _stored.length ? _stored : (this.tabData$$()?.sortFields ?? []);
    });
    get SortFields(): ISortField[] {return this.sortFieldsStored$$();}
    set SortFields(value: ISortField[]) {
        const _current = this.__appLocalStorageService.appData$$.listSortFields() ?? {};
        this.__appLocalStorageService.appData$$.listSortFields.set({ ..._current, [this.__listName]: value });
    }

    // Volledige weergavevolgorde van ALLE sorteerbare velden (actief + inactief) voor de
    // instellingen-UI — los van sortFieldsStored$$ hierboven, dat alleen de actieve keten bevat.
    // Verandert bewust alléén wanneer een veld NIEUW geactiveerd wordt (zie sortField_toggle
    // hieronder): een reset laat deze volgorde ongemoeid, zodat de instellingenlijst niet terugspringt
    // naar de statische searchFields-declaratievolgorde zodra een veld uit de keten valt. Onbekende
    // velden (nog nooit gezien, of na een config-wijziging) worden achteraan aangevuld; verdwenen
    // velden eruit gefilterd.
    fieldOrder$$ = computed<string[]>(() => {
        const _stored = this.__appLocalStorageService.appData$$.listFieldOrder()?.[this.__listName];
        const _all = this.searchFields$$();
        if (!_stored?.length) return _all;

        const _known = _stored.filter(f => _all.includes(f));
        const _new = _all.filter(f => !_known.includes(f));
        return [..._known, ..._new];
    });

    private __fieldOrder_set(order: string[]): void {
        const _current = this.__appLocalStorageService.appData$$.listFieldOrder() ?? {};
        this.__appLocalStorageService.appData$$.listFieldOrder.set({ ..._current, [this.__listName]: order });
    }

    // 3-staps klikpatroon per veld: niet actief → ascending (toegevoegd aan einde keten) → descending
    // (zelfde positie) → reset: dit veld én alles ná dit veld (lager in prioriteit) valt uit de keten,
    // alles vóór dit veld (hogere prioriteit) blijft ongewijzigd staan. Voor het bovenste/primaire veld
    // (index 0) betekent dit automatisch dat de hele keten leegloopt → terug naar de tab-default (zie
    // de fallback in sortFields$$ hierboven) — geen aparte regel nodig, dat is gewoon het randgeval
    // van dezelfde slice. Werkt op sortFieldsStored$$ (niet de effectieve sortFields$$!), anders
    // botst de tab-default hierboven weer met de klik-cyclus, zie de uitleg bij sortFieldsStored$$.
    sortField_toggle(field: string) {
        const _current = this.sortFieldsStored$$();
        const _index = _current.findIndex(s => s.field === field);

        if (_index === -1) {
            const _nextChain = [..._current, { field, direction: 'asc' as const }];
            this.SortFields = _nextChain;
            // Nieuw geactiveerd veld schuift naar de plek ná de al-actieve velden (vóór de inactieve
            // rest, in hun bestaande relatieve volgorde) — zie fieldOrder$$ hierboven.
            const _activeNames = _nextChain.map(s => s.field);
            const _rest = this.fieldOrder$$().filter(f => !_activeNames.includes(f));
            this.__fieldOrder_set([..._activeNames, ..._rest]);
        } else if (_current[_index].direction === 'asc') {
            this.SortFields = _current.map((s, i) => i === _index ? { ...s, direction: 'desc' } : s);
        } else {
            this.SortFields = _current.slice(0, _index);
        }
    }

    constructor() {
        const __tabIndex = this.__tabListService.tabIndex$$();
        const __tabData = this.__tabListService.tabList$$()[__tabIndex] ?? null;
        
        this.__tabData$.next(__tabData);

    }
}

